
/**
 * Performance monitoring utility to track and report site performance metrics
 */

interface PerformanceMetrics {
  timeToInteractive?: number;
  firstContentfulPaint?: number;
  domContentLoaded?: number;
  loadComplete?: number;
  navigationTimings?: Record<string, number>;
  resourceTimings: ResourceTiming[];
  errors: string[];
}

interface ResourceTiming {
  name: string;
  duration: number;
  transferSize?: number;
  initiatorType: string;
}

// Global state to track performance metrics
const performanceState: PerformanceMetrics = {
  resourceTimings: [],
  errors: []
};

/**
 * Collect performance metrics for the current page
 */
export const collectPerformanceMetrics = (): PerformanceMetrics => {
  try {
    if (typeof window === 'undefined' || !window.performance) {
      return performanceState;
    }

    const navigationEntries = window.performance.getEntriesByType('navigation');
    const navEntry = navigationEntries.length > 0 ? navigationEntries[0] as PerformanceNavigationTiming : null;

    // Collect basic timing metrics
    const metrics: PerformanceMetrics = {
      resourceTimings: [],
      errors: []
    };

    if (navEntry) {
      metrics.domContentLoaded = navEntry.domContentLoadedEventEnd;
      metrics.loadComplete = navEntry.loadEventEnd;

      // Navigation timings breakdown
      metrics.navigationTimings = {
        redirect: navEntry.redirectEnd - navEntry.redirectStart,
        dns: navEntry.domainLookupEnd - navEntry.domainLookupStart,
        connect: navEntry.connectEnd - navEntry.connectStart,
        request: navEntry.responseStart - navEntry.requestStart,
        response: navEntry.responseEnd - navEntry.responseStart,
        processing: navEntry.loadEventStart - navEntry.responseEnd,
      };
    }

    // Collect important paint metrics
    const paintEntries = window.performance.getEntriesByType('paint');
    for (const entry of paintEntries) {
      const paintEntry = entry as PerformancePaintTiming;
      if (paintEntry.name === 'first-contentful-paint') {
        metrics.firstContentfulPaint = paintEntry.startTime;
      }
    }

    // Time to Interactive - approximation based on load event
    // Note: For a more accurate TTI, consider using the web-vitals library
    metrics.timeToInteractive = navEntry?.loadEventEnd || 0;

    // Collect resource timings for large resources
    const resourceEntries = window.performance.getEntriesByType('resource');
    metrics.resourceTimings = resourceEntries
      .filter(entry => (entry as PerformanceResourceTiming).duration > 100)
      .map((entry) => {
        const resourceEntry = entry as PerformanceResourceTiming;
        return {
          name: resourceEntry.name,
          duration: Math.round(resourceEntry.duration),
          transferSize: resourceEntry.transferSize,
          initiatorType: resourceEntry.initiatorType
        };
      })
      // Sort by duration (slowest first)
      .sort((a, b) => b.duration - a.duration)
      // Limit to top 10 slowest resources
      .slice(0, 10);

    // Store in global state and return
    Object.assign(performanceState, metrics);
    return metrics;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    performanceState.errors.push(`Performance monitoring error: ${errorMessage}`);
    console.error('Error collecting performance metrics:', error);
    return performanceState;
  }
};

/**
 * Get the current performance metrics
 */
export const getPerformanceMetrics = (): PerformanceMetrics => {
  return { ...performanceState };
};

/**
 * Log performance metrics to console
 */
export const logPerformanceMetrics = () => {
  const metrics = collectPerformanceMetrics();
  console.log('📊 Performance Metrics:', {
    'Time to Interactive': `${Math.round(metrics.timeToInteractive || 0)}ms`,
    'First Contentful Paint': `${Math.round(metrics.firstContentfulPaint || 0)}ms`,
    'DOM Content Loaded': `${Math.round(metrics.domContentLoaded || 0)}ms`,
    'Load Complete': `${Math.round(metrics.loadComplete || 0)}ms`,
    'Navigation Timings': metrics.navigationTimings,
    'Slow Resources': metrics.resourceTimings.map(r => ({
      name: r.name.split('/').pop()?.split('?')[0] || r.name,
      duration: `${r.duration}ms`,
      size: r.transferSize ? `${Math.round(r.transferSize / 1024)}KB` : 'Unknown',
      type: r.initiatorType
    }))
  });
  return metrics;
};

// Initialize performance monitoring
export const initializePerformanceMonitoring = () => {
  // Collect metrics after page load
  if (typeof window !== 'undefined') {
    window.addEventListener('load', () => {
      setTimeout(() => {
        logPerformanceMetrics();
      }, 1000);
    });
  }

  // Expose performance metrics globally for debugging
  if (typeof window !== 'undefined') {
    window._performanceMetrics = {
      getMetrics: getPerformanceMetrics,
      logMetrics: logPerformanceMetrics
    };
  }

  return {
    getMetrics: getPerformanceMetrics,
    logMetrics: logPerformanceMetrics
  };
};

// Add type definition for global performance monitoring
declare global {
  interface Window {
    _performanceMetrics?: {
      getMetrics: () => PerformanceMetrics;
      logMetrics: () => PerformanceMetrics;
    };
  }
}

export default {
  initializePerformanceMonitoring,
  collectPerformanceMetrics,
  getPerformanceMetrics,
  logPerformanceMetrics
};
